import { FC, useMemo } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { documentTypeOptions } from '@features/workspace/views/document/modals/upload-documents-modal/index';

const useStyles = makeStyles<any>()({
  fileUploadStatusIcon: {
    width: 42,
    height: 42,
    borderRadius: 15,
    '&.failed': {
      backgroundColor: '#c95e3c38',
      '& color': {
        fill: '#c95e3c',
      },
    },
    '&.pending': {
      backgroundColor: '#c95e3c38',
      '& color': {
        fill: '#c95e3c',
      },
    },
    '&.success': {
      backgroundColor: '#1aae4e38',
      '& color': {
        fill: '#1aae4e',
      },
    },
  },
  progressBar: {
    backgroundColor: '#F6F6F7 !important',
    width: '100%',
    borderRadius: '4px',
    '&.pending .MuiLinearProgress-bar': {
      backgroundColor: '#c95e3c',
    },
    '&.success .MuiLinearProgress-bar': {
      backgroundColor: '#1aae4e',
    },
  },
  fileTitle: {
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 400,
    color: '#4B4B4B',
  },
  documentMeta: {
    fontSize: '10px',
    lineHeight: '16px',
    fontWeight: 400,
    color: '#838383',
  },
});

export enum FileUploadStatusEnum {
  Pending,
  Success,
  Failed,
}

type FileUploadStatusProps = {
  filename: string;
  status: FileUploadStatusEnum;
  progress: number | null;
  onRetry: () => void;
  onDelete: () => void;
  documentType: string;
  contactName?: string;
};

const UploadStatusIcon: FC<{ status: FileUploadStatusEnum }> = ({
  status,
}: {
  status: FileUploadStatusEnum;
}) => {
  const { classes } = useStyles(null);

  return (
    <>
      {status === FileUploadStatusEnum.Success && (
        <Box
          alignItems="center"
          className={clsx(classes.fileUploadStatusIcon, 'success')}
          display="flex"
          justifyContent="center"
        >
          <i className="ri-check-fill" style={{ fontSize: 24 }} />
        </Box>
      )}
      {status === FileUploadStatusEnum.Pending && (
        <Box
          alignItems="center"
          className={clsx(classes.fileUploadStatusIcon, 'pending')}
          display="flex"
          justifyContent="center"
        >
          <i className="ri-file-upload-fill" style={{ fontSize: 24 }} />
        </Box>
      )}
      {status === FileUploadStatusEnum.Failed && (
        <Box
          alignItems="center"
          className={clsx(classes.fileUploadStatusIcon, 'failed')}
          display="flex"
          justifyContent="center"
        >
          <i className="ri-error-warning-fill" style={{ fontSize: 24 }} />
        </Box>
      )}
    </>
  );
};

const FileUploadStatus: FC<FileUploadStatusProps> = ({
  filename,
  status,
  progress,
  onRetry,
  onDelete,
  documentType,
  contactName,
}) => {
  const { classes } = useStyles(null);

  const documentTypeName = useMemo(() => {
    return documentTypeOptions.find((opt: any) => opt.value === documentType)
      ?.label;
  }, [documentType]);

  return (
    <Box display="flex" flexGrow={1}>
      {/* Icon column */}
      <Box alignItems="center" display="flex" height="100%">
        <UploadStatusIcon status={status} />
      </Box>
      {/* 2nd column */}
      <Box display="flex" flexDirection="column" flexGrow={1} ml={2.5}>
        {/* File name and close button line */}
        <Box
          alignItems="center"
          display="flex"
          flexGrow={1}
          justifyContent="space-between"
        >
          <Box className={classes.fileTitle} display="flex">
            {filename}
          </Box>
          <Box alignItems="center" display="flex">
            {status === FileUploadStatusEnum.Failed && (
              <Box display="flex">
                <i
                  className="ri-restart-line"
                  role="presentation"
                  style={{
                    fontSize: 16,
                    color: '#83838338',
                    cursor: 'pointer',
                  }}
                  onClick={onRetry}
                />
              </Box>
            )}
            <Box display="flex" ml={1.4}>
              <i
                className="ri-close-fill"
                role="presentation"
                style={{ fontSize: 20, cursor: 'pointer', color: '#83838338' }}
                onClick={onDelete}
              />
            </Box>
          </Box>
        </Box>
        {/* Progress line */}
        <Box display="flex" mt={1.5}>
          {status !== FileUploadStatusEnum.Failed ? (
            <LinearProgress
              className={clsx(
                classes.progressBar,
                status === FileUploadStatusEnum.Pending ? 'pending' : 'success'
              )}
              value={progress || 1}
              variant="determinate"
            />
          ) : (
            <span
              style={{
                color: '#FF7C5C',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
              }}
            >
              Upload failed
            </span>
          )}
        </Box>
        {/* File type & customer name line */}
        <Box
          className={classes.documentMeta}
          display="flex"
          justifyContent="space-between"
          mt={1}
        >
          <Box display="flex">{documentTypeName}</Box>
          {!!contactName && <Box display="flex">{contactName}</Box>}
        </Box>
      </Box>
    </Box>
  );
};

export default FileUploadStatus;
